/**
 * NumberBrandMappingCard — Multi-brand operator setup.
 *
 * For operators running two brands off two numbers (e.g. SellYourHomePA
 * on a 215 local + TPAcquisitions on an 877 toll-free), this card lets
 * them map each owned number to its correct brand + website. Jarvis then
 * signs off with the right brand automatically based on which number the
 * seller texted.
 *
 * Data flow:
 *   GET  /api/twilio-numbers/number-brand           → { items: [...] }
 *   PATCH /api/twilio-numbers/number-brand/{phone}  → refreshed list
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Building2, Globe, Check, Sparkles, PhoneCall, Trash2,
} from "lucide-react";

const SOURCE_BADGE = {
  managed: { label: "MANAGED",   bg: "bg-emerald-100", fg: "text-emerald-800", border: "border-emerald-200" },
  byoa:    { label: "BYOA",      bg: "bg-slate-100",   fg: "text-slate-700",   border: "border-slate-200" },
};

function formatE164(p) {
  const s = String(p || "").replace(/\D/g, "");
  if (s.length === 11 && s.startsWith("1")) {
    return `+1 (${s.slice(1, 4)}) ${s.slice(4, 7)}-${s.slice(7)}`;
  }
  return p || "";
}

export default function NumberBrandMappingCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({}); // phone → {brand_label, brand_website}
  const [savingPhone, setSavingPhone] = useState(null);
  const [archivingPhone, setArchivingPhone] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/twilio-numbers/number-brand");
      setItems(data.items || []);
      // hydrate drafts with current values
      const next = {};
      for (const it of data.items || []) {
        next[it.phone_number] = {
          brand_label:   it.brand_label   || "",
          brand_website: it.brand_website || "",
        };
      }
      setDrafts(next);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load your numbers");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setField = (phone, field, value) => {
    setDrafts((d) => ({ ...d, [phone]: { ...(d[phone] || {}), [field]: value } }));
  };

  const save = async (phone) => {
    setSavingPhone(phone);
    try {
      const body = drafts[phone] || {};
      const { data } = await api.patch(
        `/twilio-numbers/number-brand/${encodeURIComponent(phone)}`,
        {
          brand_label:   body.brand_label   || "",
          brand_website: body.brand_website || "",
        },
      );
      setItems(data.items || []);
      toast.success("Brand mapping saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSavingPhone(null);
    }
  };

  const archive = async (phone, displayLabel) => {
    // Hard confirm — this hides the row from mapping AND stops inbound
    // routing to it. Reversible only via direct DB access.
    if (!window.confirm(
      `Remove ${displayLabel} from the Multi-Brand mapping?\n\n` +
      `This hides the row and stops Jarvis + inbound webhooks from routing to it. ` +
      `Use this to clean up duplicate or malformed entries. ` +
      `Your Twilio number itself is NOT released.`
    )) return;
    setArchivingPhone(phone);
    try {
      const { data } = await api.delete(
        `/twilio-numbers/number-brand/${encodeURIComponent(phone)}`,
      );
      setItems(data.items || []);
      toast.success("Removed from mapping");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Remove failed");
    } finally {
      setArchivingPhone(null);
    }
  };

  if (loading) {
    return (
      <div className="card" data-testid="number-brand-mapping-card">
        <div className="flex items-center gap-2 text-sm text-[var(--ink-3)]">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading numbers…
        </div>
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="card" data-testid="number-brand-mapping-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0 bg-slate-100 text-slate-500">
            <PhoneCall size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="label-eyebrow inline-flex items-center gap-1.5">
              <Sparkles size={11} /> Multi-Brand · Number → Brand
            </div>
            <h3 className="font-display font-bold text-lg tracking-tight mt-0.5">
              No numbers to map yet
            </h3>
            <p className="text-sm text-[var(--ink-2)] mt-1">
              Once you purchase a Managed Number or connect a BYOA number,
              you can map each one to a specific brand here.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card" data-testid="number-brand-mapping-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0 bg-amber-500/10 text-amber-700">
          <Building2 size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="label-eyebrow inline-flex items-center gap-1.5">
            <Sparkles size={11} /> Multi-Brand · Number → Brand
          </div>
          <h3 className="font-display font-bold text-lg tracking-tight mt-0.5">
            Map each number to its brand
          </h3>
          <p className="text-sm text-[var(--ink-2)] mt-1">
            Running two brands off two numbers? Set the brand + website for
            each below and Jarvis will sign off correctly based on which
            number the seller texted. Leave blank to fall back to your
            workspace brand.
          </p>
        </div>
      </div>

      <ul className="mt-5 space-y-3" data-testid="number-brand-list">
        {items.map((it) => {
          const badge = SOURCE_BADGE[it.source] || SOURCE_BADGE.byoa;
          const draft = drafts[it.phone_number] || {};
          const dirty =
            (draft.brand_label   || "") !== (it.brand_label   || "") ||
            (draft.brand_website || "") !== (it.brand_website || "");
          return (
            <li
              key={it.phone_number}
              data-testid={`number-brand-row-${it.phone_number}`}
              className="rounded-lg border border-slate-200 bg-white p-3"
            >
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-[13.5px] font-black tabular-nums text-slate-900">
                  {formatE164(it.phone_number)}
                </div>
                <span
                  className={`text-[9.5px] uppercase tracking-widest font-black rounded-full px-1.5 py-0.5 border ${badge.bg} ${badge.fg} ${badge.border}`}
                >
                  {badge.label}
                </span>
                {it.friendly_name && (
                  <span className="text-[11px] text-slate-500">
                    {it.friendly_name}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => archive(
                    it.phone_number,
                    it.friendly_name || formatE164(it.phone_number),
                  )}
                  disabled={archivingPhone === it.phone_number}
                  data-testid={`number-brand-remove-${it.phone_number}`}
                  title="Remove this row from the Multi-Brand mapping (does NOT release the Twilio number)"
                  className="ml-auto inline-flex items-center gap-1 rounded-md border border-rose-200 bg-white text-[10.5px] font-semibold text-rose-700 px-2 py-0.5 hover:bg-rose-50 disabled:opacity-50"
                >
                  {archivingPhone === it.phone_number ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Trash2 className="w-3 h-3" />
                  )}
                  Remove
                </button>
              </div>

              <div className="grid sm:grid-cols-[2fr_2fr_auto] gap-2 mt-3 items-end">
                <label className="block">
                  <div className="text-[9.5px] uppercase tracking-widest font-black text-slate-500 mb-1 inline-flex items-center gap-1">
                    <Building2 size={10} /> Brand label
                  </div>
                  <input
                    type="text"
                    value={draft.brand_label || ""}
                    onChange={(e) => setField(it.phone_number, "brand_label", e.target.value)}
                    placeholder="SellYourHomePA"
                    data-testid={`number-brand-label-${it.phone_number}`}
                    className="w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
                  />
                </label>
                <label className="block">
                  <div className="text-[9.5px] uppercase tracking-widest font-black text-slate-500 mb-1 inline-flex items-center gap-1">
                    <Globe size={10} /> Website (optional)
                  </div>
                  <input
                    type="text"
                    value={draft.brand_website || ""}
                    onChange={(e) => setField(it.phone_number, "brand_website", e.target.value)}
                    placeholder="sellyourhomepa.com"
                    data-testid={`number-brand-website-${it.phone_number}`}
                    className="w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => save(it.phone_number)}
                  disabled={!dirty || savingPhone === it.phone_number}
                  data-testid={`number-brand-save-${it.phone_number}`}
                  className="inline-flex items-center gap-1 rounded-md text-white text-[11.5px] font-bold px-3 py-1.5 shadow-md disabled:opacity-40"
                  style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}
                >
                  {savingPhone === it.phone_number ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Check className="w-3 h-3" />
                  )}
                  Save
                </button>
              </div>

              {/* Preview */}
              {(draft.brand_label || "").trim() && (
                <div className="mt-3 rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-[11.5px] text-slate-600 italic">
                  Jarvis on this number: &ldquo;Hi, this is <b>Jordan</b> with{" "}
                  <b>{draft.brand_label}</b> — quick question about your property.&rdquo;
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
